/**
 * Autonomous Run store — tracks live state of Felix goal-mode runs and
 * the user's Follow Mode preference.
 *
 * The store is fed from two sources:
 * 1. Cross-tab SSE events (`autonomous.goal_started`, etc.) via useSSE —
 *    visible in every browser tab watching the project.
 * 2. Direct-stream events from the autonomous endpoint (the tab that
 *    initiated the run) — same shape, different transport.
 *
 * Updates are keyed by `goalRunId` + `waveNumber` so duplicate events
 * from both sources don't double-count.
 *
 * Follow Mode is persisted to localStorage (`ftl_follow_mode`). The
 * active run state is in-memory only — cleared on a terminal event or
 * on next goal start.
 */

import { create } from "zustand";

export interface AutonomousEntityCreated {
  goalRunId: string;
  waveNumber: number;
  positionInWave: number;
  waveTotal: number;
  entityType: string;
  entityId: string;
  title: string;
  tool: string;
  action: "create" | "update";
  createdAt: number; // epoch ms for ordering + age-out animations
}

export interface AutonomousWave {
  waveNumber: number;
  toolCount: number;
  startedAt: number;
  completedAt?: number;
  elapsedMs?: number;
  succeeded?: number;
}

export interface AutonomousRun {
  goalRunId: string;
  startedAt: number;
  messagePreview?: string;
  status: "running" | "completed" | "failed" | "cancelled";
  waves: AutonomousWave[];
  entities: AutonomousEntityCreated[];
  totalActions?: number;
  totalEntitiesCreated?: number;
  elapsedMs?: number;
  stopReason?: string | null;
}

interface AutonomousRunState {
  // Active run (one at a time per browser). Null when idle.
  activeRun: AutonomousRun | null;

  // Persisted preference — per-user localStorage.
  followMode: boolean;

  // Indicator UI state — expanded / collapsed.
  indicatorExpanded: boolean;

  // Actions driven from SSE or direct-stream events.
  startRun: (params: {
    goalRunId: string;
    startedAt?: string;
    messagePreview?: string;
  }) => void;
  markWaveStarted: (params: {
    goalRunId: string;
    waveNumber: number;
    toolCount: number;
  }) => void;
  appendEntity: (params: AutonomousEntityCreated) => void;
  markWaveCompleted: (params: {
    goalRunId: string;
    waveNumber: number;
    elapsedMs: number;
    succeeded?: number;
  }) => void;
  completeRun: (params: {
    goalRunId: string;
    totalWaves?: number;
    totalEntitiesCreated?: number;
    totalActions?: number;
    elapsedMs?: number;
    stopReason?: string | null;
  }) => void;
  failRun: (params: {
    goalRunId: string;
    totalWaves?: number;
    totalActions?: number;
    elapsedMs?: number;
    stopReason?: string | null;
  }) => void;
  // Wave 10D — cooperative cancel observed (from SSE or local response).
  cancelRun: (params: {
    goalRunId: string;
    totalWaves?: number;
    totalEntitiesCreated?: number;
    totalActions?: number;
    elapsedMs?: number;
    stopReason?: string | null;
  }) => void;
  // Transient UI flag set while the Cancel POST is in flight so the
  // button can show a spinner and disable itself.
  cancelInFlight: boolean;
  setCancelInFlight: (inFlight: boolean) => void;
  dismissRun: () => void;

  // UI + preference
  setFollowMode: (on: boolean) => void;
  setIndicatorExpanded: (expanded: boolean) => void;
}

const FOLLOW_MODE_KEY = "ftl_follow_mode";

function readFollowMode(): boolean {
  if (typeof localStorage === "undefined") return true;
  const v = localStorage.getItem(FOLLOW_MODE_KEY);
  // Default ON — per the Wave 10C UX plan, Follow Mode is the hero
  // experience. Users who find it distracting toggle it off.
  if (v === null) return true;
  return v === "true";
}

function writeFollowMode(on: boolean) {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(FOLLOW_MODE_KEY, String(on));
}

export const useAutonomousRunStore = create<AutonomousRunState>((set) => ({
  activeRun: null,
  followMode: readFollowMode(),
  indicatorExpanded: false,

  startRun: ({ goalRunId, startedAt, messagePreview }) =>
    set((state) => {
      // Ignore re-entry for same goal (SSE + direct stream can both emit).
      if (state.activeRun && state.activeRun.goalRunId === goalRunId) {
        return state;
      }
      return {
        activeRun: {
          goalRunId,
          startedAt: startedAt ? new Date(startedAt).getTime() : Date.now(),
          messagePreview,
          status: "running",
          waves: [],
          entities: [],
        },
      };
    }),

  markWaveStarted: ({ goalRunId, waveNumber, toolCount }) =>
    set((state) => {
      if (!state.activeRun || state.activeRun.goalRunId !== goalRunId) {
        return state;
      }
      // Idempotent: skip if we already have this wave.
      if (state.activeRun.waves.some((w) => w.waveNumber === waveNumber)) {
        return state;
      }
      return {
        activeRun: {
          ...state.activeRun,
          waves: [
            ...state.activeRun.waves,
            { waveNumber, toolCount, startedAt: Date.now() },
          ],
        },
      };
    }),

  appendEntity: (entity) =>
    set((state) => {
      if (!state.activeRun || state.activeRun.goalRunId !== entity.goalRunId) {
        return state;
      }
      // Dedupe by entityId — the same entity may arrive via both streams.
      if (state.activeRun.entities.some((e) => e.entityId === entity.entityId)) {
        return state;
      }
      return {
        activeRun: {
          ...state.activeRun,
          entities: [...state.activeRun.entities, entity],
        },
      };
    }),

  markWaveCompleted: ({ goalRunId, waveNumber, elapsedMs, succeeded }) =>
    set((state) => {
      if (!state.activeRun || state.activeRun.goalRunId !== goalRunId) {
        return state;
      }
      return {
        activeRun: {
          ...state.activeRun,
          waves: state.activeRun.waves.map((w) =>
            w.waveNumber === waveNumber
              ? { ...w, completedAt: Date.now(), elapsedMs, succeeded }
              : w
          ),
        },
      };
    }),

  completeRun: ({
    goalRunId,
    totalWaves,
    totalEntitiesCreated,
    totalActions,
    elapsedMs,
    stopReason,
  }) =>
    set((state) => {
      if (!state.activeRun || state.activeRun.goalRunId !== goalRunId) {
        return state;
      }
      return {
        activeRun: {
          ...state.activeRun,
          status: "completed",
          totalEntitiesCreated: totalEntitiesCreated ?? state.activeRun.entities.length,
          totalActions,
          elapsedMs,
          stopReason,
          // waves array may be shorter than totalWaves if events dropped
          // — prefer the server-reported count when present
          waves: totalWaves && totalWaves > state.activeRun.waves.length
            ? state.activeRun.waves
            : state.activeRun.waves,
        },
      };
    }),

  failRun: ({ goalRunId, totalWaves, totalActions, elapsedMs, stopReason }) =>
    set((state) => {
      if (!state.activeRun || state.activeRun.goalRunId !== goalRunId) {
        return state;
      }
      return {
        activeRun: {
          ...state.activeRun,
          status: "failed",
          totalActions,
          totalEntitiesCreated: state.activeRun.entities.length,
          elapsedMs,
          stopReason,
          waves: totalWaves && totalWaves > state.activeRun.waves.length
            ? state.activeRun.waves
            : state.activeRun.waves,
        },
      };
    }),

  cancelRun: ({
    goalRunId,
    totalWaves,
    totalEntitiesCreated,
    totalActions,
    elapsedMs,
    stopReason,
  }) =>
    set((state) => {
      if (!state.activeRun || state.activeRun.goalRunId !== goalRunId) {
        return state;
      }
      return {
        activeRun: {
          ...state.activeRun,
          status: "cancelled",
          totalActions,
          totalEntitiesCreated:
            totalEntitiesCreated ?? state.activeRun.entities.length,
          elapsedMs,
          stopReason: stopReason ?? "user_cancel",
          waves:
            totalWaves && totalWaves > state.activeRun.waves.length
              ? state.activeRun.waves
              : state.activeRun.waves,
        },
        cancelInFlight: false,
      };
    }),

  cancelInFlight: false,
  setCancelInFlight: (inFlight) => set({ cancelInFlight: inFlight }),

  dismissRun: () =>
    set({ activeRun: null, indicatorExpanded: false, cancelInFlight: false }),

  setFollowMode: (on) => {
    writeFollowMode(on);
    set({ followMode: on });
  },

  setIndicatorExpanded: (expanded) => set({ indicatorExpanded: expanded }),
}));

/** Maps an entity_type from the backend to a list page path. Used by
 * Follow Mode auto-navigation. Returns null for types without a
 * dedicated list page. */
export function entityTypeToListPath(
  projectId: string | undefined,
  entityType: string
): string | null {
  if (!projectId) return null;
  const map: Record<string, string> = {
    objective: `/projects/${projectId}/objectives`,
    user_story: `/projects/${projectId}/user-stories`,
    impl_task: `/projects/${projectId}/tasks`,
    task: `/projects/${projectId}/tasks`,
    test_case: `/projects/${projectId}/test-cases`,
    component: `/projects/${projectId}/components`,
    defect: `/projects/${projectId}/defects`,
    sprint: `/projects/${projectId}/sprints`,
    document: `/projects/${projectId}/documents`,
  };
  return map[entityType] ?? null;
}
